import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { 
  ScaleIcon, 
  MicrophoneIcon, 
  DocumentTextIcon,
  ClockIcon,
  ShieldCheckIcon
} from '../icons';

// Ghanaian legal phrases for hero demonstration
const heroLegalPhrases = [
  "Based on the evidence presented and the provisions of Act 299...",
  "Under Section 42 of the Criminal Offences Act of 1960...",
  "The court finds that the burden of proof rests upon the plaintiff...",
  "Pursuant to the rules of natural justice as established in...",
  "In accordance with Article 19(3) of the Constitution..."
];

export function HeroCourtAnimation() {
  const ref = useRef(null);
  const isInView = useInView(ref);
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [transcriptionText, setTranscriptionText] = useState('');
  const [speakerLabel, setSpeakerLabel] = useState('Hon. Justice');

  useEffect(() => {
    const timeoutIds = [];

    if (isInView) {
      // Start recording animation
      timeoutIds.push(setTimeout(() => {
        setIsRecording(true);
      }, 500));

      // Start transcription simulation
      const transcriptionInterval = setInterval(() => {
        const fullPhrase = heroLegalPhrases[currentPhrase];
        const currentLength = transcriptionText.length;
        
        if (currentLength < fullPhrase.length) {
          setTranscriptionText(fullPhrase.substring(0, currentLength + 2));
        } else {
          // Move to next phrase after a pause
          timeoutIds.push(setTimeout(() => {
            setCurrentPhrase((prev) => (prev + 1) % heroLegalPhrases.length);
            setTranscriptionText('');
            setSpeakerLabel(prev => {
              const labels = ['Hon. Justice', 'Counsel for Plaintiff', 'Counsel for Defendant', 'Witness'];
              return labels[Math.floor(Math.random() * labels.length)];
            });
          }, 2000));
        }
      }, 100);

      timeoutIds.push(transcriptionInterval);

      // Recording pulse animation
      const pulseInterval = setInterval(() => {
        setIsRecording(prev => !prev);
      }, 2000);
      timeoutIds.push(pulseInterval);

    } else {
      setIsRecording(false);
      setTranscriptionText('');
      setCurrentPhrase(0);
    }

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [isInView, currentPhrase, transcriptionText]);

  return (
    <div
      ref={ref}
      className="w-full h-full relative flex items-center justify-center overflow-hidden gradient-legal rounded-2xl border border-legal-200"
    >
      {/* Main transcription display */}
      <div className="relative w-full max-w-md mx-auto px-6 py-8">
        {/* Court header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <img 
              src="/logo.png" 
              alt="Snwolley CourtScribe Logo" 
              className="h-10 w-10 rounded-lg shadow-lg"
            />
            <div>
              <div className="text-xs font-bold text-legal-700">SUPREME COURT OF GHANA</div>
              <div className="text-xs text-legal-500">Live AI Transcription</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isRecording && (
              <>
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-red-600">Recording</span>
              </>
            )}
          </div>
        </div>

        {/* Transcription text area */}
        <div className="bg-white rounded-xl shadow-lg border border-legal-200 p-6 mb-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-legal-700">{speakerLabel}:</span>
            <div className="flex items-center space-x-2 text-xs text-court-600">
              <ClockIcon className="h-3 w-3" />
              <span>{new Date().toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}</span>
            </div>
          </div>
          
          <div className="min-h-[120px] text-sm text-legal-800 leading-relaxed font-serif">
            <span className="text-gray-400">{transcriptionText || 'Waiting for transcription...'}</span>
            {transcriptionText && <span className="inline-block w-1 h-5 bg-court-500 ml-1 animate-pulse"></span>}
          </div>
        </div>

        {/* AI insights panel */}
        <div className="bg-gradient-to-r from-accent-50 to-court-50 rounded-xl border border-legal-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ShieldCheckIcon className="h-4 w-4 text-accent-600" />
              <span className="text-xs font-bold text-legal-700">AI Analysis Active</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-xs text-legal-600">99.5% Accurate</span>
              </div>
              <div className="flex items-center space-x-1">
                <MicrophoneIcon className="h-3 w-3 text-court-600" />
                <span className="text-xs text-legal-600">Live</span>
              </div>
            </div>
          </div>
          
          <div className="mt-2 text-xs text-legal-600">
            <div>✓ Legal terminology detected</div>
            <div>✓ Speaker identification enabled</div>
            <div>✓ Ghana legal context active</div>
          </div>
        </div>

        {/* Bottom status bar */}
        <div className="mt-4 flex items-center justify-between text-xs text-legal-500">
          <span>Case #2025/SC/GHA/1234</span>
          <span>AI-Powered Transcription</span>
        </div>
      </div>

      {/* Floating elements */}
      <AnimatePresence>
        {isInView && (
          <>
            {/* Floating transcript bubbles */}
            <motion.div
              className="absolute top-4 right-4 bg-white rounded-lg shadow-md px-3 py-2 text-xs border border-legal-200"
              initial={{ opacity: 0, scale: 0, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1 }}
            >
              <div className="flex items-center space-x-1">
                <DocumentTextIcon className="h-3 w-3 text-court-600" />
                <span className="text-legal-700">Document ready</span>
              </div>
            </motion.div>

            {/* AI processing indicator */}
            <motion.div
              className="absolute bottom-4 left-4 bg-accent-500 text-white rounded-lg shadow-md px-3 py-2 text-xs"
              initial={{ opacity: 0, scale: 0, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.5 }}
            >
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span>AI Processing</span>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}